import { useMutation } from '@apollo/client';
import type { UpdateDocumentOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateDocumentOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateDocumentOversightResultInput } from './useUpdateDocumentOversightResultTRPC';
import { useUpdateDocumentOversightResultTRPC } from './useUpdateDocumentOversightResultTRPC';

export const useUpdateDocumentOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateDocumentOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'document_oversight_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateDocumentOversightResultTRPC();

  const updateDocumentOversightResult = async (
    variables: UpdateDocumentOversightResultInput
  ): Promise<UpdateDocumentOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateDocumentOversightResult(variables);
    }

    const result = await updateGraphQL({
      variables: {
        Id: variables.Id,
        Rating: variables.Rating,
        Rationale: variables.Rationale,
        TestDate: variables.TestDate,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update document oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateDocumentOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateDocumentOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
